import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

// mui import
import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material";
import { Add } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CollectionsIcon from '@mui/icons-material/Collections';
import PsychologyIcon from '@mui/icons-material/Psychology';

// project import
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { getLocalDate } from "../../utilities/helpers";
import { setInspection } from "../../state";
import { COMMODITY_TENANT_LIST } from "../../utilities/constants";

import assignmentApi from "../../apis/assignmentApi";
import StarRateIcon from '@mui/icons-material/StarRate';
import { red, yellow, green, blue } from '@mui/material/colors';
import ModalDialog from "../../components/ModalDialog";
import ChatWithFiles from "./ChatWithFiles";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import ExhibitionManager from "./ExhibitionManager";

const Assignments = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = tokens(theme.palette.mode);
  const [isLoading, setIsLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [openExhibitionModal, setOpenExhibitionModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const loggedInUser = useSelector((state) => {
    return state.user;
  });

  const tenantId = useSelector((state) => {
    return state.tenantId;
  });

  const idToken = useSelector((state) => {
    return state.token;
  });

  const columns = [
    {
      field: "subject",
      headerName: "Name",
      flex: 2,
      cellClassName: "name-column--cell",
    },
    {
      field: "commodity_id",
      headerName: "Commodity",
      flex: 1,
      renderCell: ({ row }) => {
        const commodity = COMMODITY_TENANT_LIST.find((item) => item.id === row.commodity_id);
        return (
          <Box>
            {commodity ? commodity.description : 'N/A'}
          </Box>
        );
      },
    },
    {
      field: "firm_name",
      headerName: "Auditing Firm",
      flex: 1,
    },
    {
      field: "firm_to_be_audited_name",
      headerName: "Firm To Be Audited",
      flex: 1,
    },
    {
      field: "priority",
      headerName: "Priority",
      flex: 0.5,
    },
    {
      field: "assignment_status",
      headerName: "Status",
      flex: 0.5,
    },
    {
      field: "target_complete_date",
      headerName: "Target Date",
      flex: 0.5,
    },
    {
      field: "rating",
      headerName: "Rating",
      flex: 0.5,
      renderCell: ({ row }) => {
        return (
          <Box>
            {row?.rating === null ? (
              <Box sx={{ fontSize: '12px', color: colors.grey[400] }}>Loading...</Box>
            ) : (
              <>
                {row?.rating <= 3 && <StarRateIcon sx={{ color: red[700]}} />}
                {row?.rating > 3 && row?.rating <= 6 && <StarRateIcon sx={{ color: yellow[700]}} />}
                {row?.rating > 6 && row?.rating <= 9 && <StarRateIcon sx={{ color: green[700]}} />}
                {row?.rating === 10 && <StarRateIcon sx={{ color: blue[700] }} />}
              </>
            )}
          </Box>
        );
      },
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
    },
    {
      field: "id",
      headerName: "Inspect",
      flex: 0.5,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Tooltip title="Inspection">
              <IconButton color="warning" onClick={() => handleInspection(row)} variant="">
                  <img src="../../assets/ieqiplogo.png" alt="test" width="30px" height="30px" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "exhibitions",
      headerName: "Exhibitions",
      flex: 0.5,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Tooltip title="Exhibitions">
              <IconButton color="secondary" onClick={() => onHandleExhibitionClick(row)} variant="">
                  <CollectionsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "files_ai",
      headerName: "Files & AI",
      flex: 0.5,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Tooltip title="Upload files & AI assistant - Manage files and ask questions">
              <IconButton color="secondary" onClick={() => onHandleFileIconClick(row)} variant="">
                  <PsychologyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "assignment_uid",
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Tooltip title="Edit">
              <IconButton
                color="success"
                aria-label="Edit"
                component={Link}
                to={`/assignments/${row.assignment_uid}/edit`}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => handleDelete(row)} variant="">
                  <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    async function fetchAssignments() {
      setIsLoading(true);
      try {
        const requestHeader = {
          headers: {
            "x-eqip-tenantid": tenantId,
            "Authorization": idToken
          },
        };

        // Super admin accounts see all assignments across all tenants
        const SUPER_ADMIN_EMAILS = ['khiemwynngate@gmail.com', 'vdkhiem81@yahoo.com'];
        const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(loggedInUser?.email?.toLowerCase());

        let assignmentList;
        if (isSuperAdmin) {
          const r = await ieqipCoreServiceApi.get(
            `/api/assignments/superadmin/all?username=${encodeURIComponent(loggedInUser?.email)}`
          );
          assignmentList = r.data;
        } else {
          const r = await assignmentApi.get(`/assignments/assignto/${loggedInUser?.email}`, requestHeader);
          assignmentList = r.data.body;
        }

        const initialData = assignmentList.map((item) => {
          item.target_complete_date = getLocalDate(item.target_complete_date);
          item.rating = null;
          return item;
        });

        setAssignments(initialData);
        setIsLoading(false);

        // Second: Load inspection details in background to calculate ratings
        if (assignmentList.length > 0) {
          fetchInspectionDetails(assignmentList);
        }

      } catch (err) {
        // Handle Error Here
        console.error(err);
        setIsLoading(false);
      }
    }

    async function fetchInspectionDetails(assignmentsData) {
      try {
        const inspectionUids = assignmentsData
          .map(item => item.inspection_uid)
          .join(',');

        const responseInspectionDetail = await ieqipCoreServiceApi.get(`/api/inspections/list/${inspectionUids}/inspectiondetails`);

        // Update assignments with calculated ratings
        setAssignments(prevAssignments => 
          prevAssignments.map((item) => {
            const rating = calculateLowestRating(
              responseInspectionDetail.data.filter(
                itemInspectionDetail => itemInspectionDetail.inspection_id === item.inspection_id
              )
            );
            return { ...item, rating };
          })
        );

      } catch (err) {
        console.error('Error loading inspection details:', err);
        // Keep assignments displayed even if rating loading fails
      }
    }

    fetchAssignments();
    // eslint-disable-next-line
  }, [loggedInUser.email, tenantId]);

  const handleDelete = async (assignment) => {
    try {
      setIsLoading(true);
      await assignmentApi.put('/assignments/disableAssignment', assignment);
      setAssignments(assignments.filter(item => item.assignment_uid !== assignment.assignment_uid));
      setIsLoading(false);
    } catch (err) {
      // Handle Error Here
      console.error(err);
      setIsLoading(false);
    }
  }

  const handleInspection = async (assignment) => {  
    try {
      dispatch(
        setInspection({
          isShowInspection: true,
          inspectionUid: assignment.inspection_uid,
          assignmentUid: assignment.assignment_uid
        })
      );
    } catch (err) {
      // Handle Error Here
      console.error(err);
    }
  }

  const calculateLowestRating = (row) => {
    let lowestRating;
    try {
      if (Array.isArray(row) && row.length > 0) {
        // Filter out non-numeric values and convert to numbers
        const numericRatings = row
          .map(quality => quality.detail?.rating || 999)
          .map(Number);
  
        if (numericRatings.length > 0) {
          lowestRating = Math.min(...numericRatings);
        } else {
          console.error('No valid numeric ratings found');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }

    return lowestRating;
  }

  // Begin Upload File modal
  const onHandleFileIconClick = (assignment) => {
    const { id } = assignment;
    setSelectedAssignment(id);
    setOpenFileModal(true);
  }
  
  const onHandleFileModalOk = () => {
    setOpenFileModal(false);
  }

  const onHandleFileModalCancel = () => {
    setOpenFileModal(false);
  }
  // End Upload File modal

  // Begin Exhibition modal
  const onHandleExhibitionClick = (assignment) => {
    setSelectedAssignment(assignment);
    setOpenExhibitionModal(true);
  }
  
  const onHandleExhibitionModalOk = () => {
    setOpenExhibitionModal(false);
  }

  const onHandleExhibitionModalCancel = () => {
    setOpenExhibitionModal(false);
  }
  // End Exhibition modal

  return (
    <Box m="20px">
      <Header title="ASSIGNMENTS" subtitle="Managing the Assignments" />
      <Box display="flex" justifyContent="end">
        <Button
          color="primary"
          variant="contained"
          startIcon={<Add />}
          component={Link}
          to="/assignments/create"
        >
          Create Assignment
        </Button>
      </Box>
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.blueAccent[600],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.grey[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.grey[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.grey[500]} !important`,
          },
        }}
      >
        <DataGrid 
          checkboxSelection={false} 
          rows={assignments} 
          columns={columns} 
          loading={isLoading}
        />
      </Box>
      <ModalDialog
          title="Files & AI"
          open={openFileModal}
          hideCancel={true}
            content={<ChatWithFiles uploadFileParams={{ 
            assignmentId: selectedAssignment }} />}
          handleOk={onHandleFileModalOk}
          handleCancel={onHandleFileModalCancel}
        />
      <ModalDialog
          title="Exhibitions"
          open={openExhibitionModal}
          hideCancel={true}
          maxWidth="xl"
          fullWidth
          content={<ExhibitionManager 
            inspectionUid={selectedAssignment?.inspection_uid}
            assignmentUid={selectedAssignment?.assignment_uid} 
          />}
          handleOk={onHandleExhibitionModalOk}
          handleCancel={onHandleExhibitionModalCancel}
        />
    </Box>
  );
};

export default Assignments;
